/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
/**
 * ProposalSelectionContext - Isolates selection state from parent re-renders.
 *
 * Selection state changes (checkbox toggles) only re-render components that
 * consume this context — not the entire ProposalDetailPanel tree.
 * Includes debounced API sync so rapid clicks batch into one request.
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { extractValue } from '@/components/proposals/shared/utils';
import { useEnsuredStoreObjects } from '@/lib/crm/hooks/useEnsuredStoreObjects';
import { getDisplayNameFromProperties } from '@/lib/crm/utils/getDisplayName';

import type { ProposedAction } from '../core/types';
import { isObjectCreatedPayload } from '../core/types';
import type { SelectionState } from '../types/selection';
import { buildActionDisplayNumbering } from '../utils/actionDisplayNumbers';
import { getCreatedEntityId } from '../utils/actionEntityRefs';
import { groupActionsByType } from '../utils/actionGrouping';
import {
  computeActionVerdicts,
  getProjectionEntityIds,
} from '../utils/actionValidity';
import { resolveEntityName } from '../utils/entityName';
import {
  describeMissingEntities,
  type MissingEntityDescription,
} from '../utils/invalidityCopy';
import {
  getNoOpActionIds,
  getNoOpPropertyKeysLive,
  type LiveRecordState,
} from '../utils/noOpActions';
import {
  AssociationSourceFetcher,
  getAssociationActionSources,
} from './AssociationSourceFetcher';
import { useActionSelection } from './useActionSelection';
import { useDebouncedSelectionSync } from './useDebouncedSelectionSync';
import { sameIdSet, useNoOpReconciliation } from './useNoOpReconciliation';

// The select-all / all-selected / some-selected exclusion is derived from the
// SELECTION-AWARE no-op set (`noOpActionIds`) at the context level, not from
// the static set the hook accepts — see the `handleSelectAll` / `isAllSelected`
// derivation below. The hook therefore gets no static exclusions. A stable
// reference keeps the hook's internal memos from busting every render.
const NO_STATIC_EXCLUSIONS: number[] = [];

interface ProposalSelectionContextValue {
  /** Selection state for passing to ActionTable / MultiActionTimeline */
  selectionState: SelectionState;
  /**
   * The action IDs that will actually run: checked AND valid against the
   * projection. This is what the preview overlay projects, what the approve
   * button counts, and what approval sends. A checked-but-invalid action is
   * NOT in here even though `selectionState.isSelected` still reports it
   * checked — that raw state is for rendering the checkbox, not for acting.
   */
  selectedActionIds: number[];
  /**
   * All actions in the proposal. Lets deeply-nested consumers (e.g. an
   * association-target picker) enumerate sibling create actions and offer their
   * proposed entities — without prop-drilling the proposal down the tree.
   */
  actions: ProposedAction[];
  /**
   * IDs of actions classified as fully no-op, live-first: while the action's
   * target record is loaded in the live CRM store, the live read decides
   * (including overriding a stale server `no_op`); otherwise the
   * server-computed `verdict` (`no_op` or `duplicate_of`) decides (see
   * `noOpActions.ts`'s `getNoOpActionIds`). Fail-open: an action with neither
   * a loaded record nor a verdict is never included.
   */
  noOpActionIds: number[];
  /** `actions` with no-op actions filtered out — what should render. */
  visibleActions: ProposedAction[];
  /**
   * Why an action can't apply, as a prefix-free clause naming what's missing
   * and the printed number of the unchecked create that would fix it —
   * "action 3 (Acme Robotics) is unchecked". `null` when the action is valid.
   * Surfaces lead with their own verb ("Won't apply – …", "Won't be linked –
   * …"); `selectionState.getDisabledReason` is the checkbox form.
   */
  describeInvalidity: (actionId: number) => string | null;
  /**
   * Live-derived no-op property keys for a single action, for per-row
   * strike-through (see `UpdateActionCard`'s diff filtering). Returns `[]`
   * for non-property-update actions, actions not found in `actions`, and
   * whenever the action's record isn't loaded in the live store yet.
   */
  getNoOpPropertyKeys: (actionId: number) => string[];
  /** Deselect multiple actions in a single state update (with dependency cascading) */
  deselectActions: (actionIds: number[]) => void;
  /**
   * Toggle all IDs in a merged action group atomically.
   * If all constituent IDs are currently selected → deselects all.
   * If any are unselected → selects all unselected ones.
   * Used by merged create+association accordion entries.
   */
  toggleMergedGroup: (ids: number[]) => void;
  /**
   * Org that owns the proposal. Published so proposal-domain consumers (e.g.
   * `ProposalPreviewProvider`, which needs the association-type schema to
   * re-type reverse association edges) can scope org-keyed queries without
   * reaching for `useOrg` — which throws outside a `TeamProvider` and would
   * make this subtree unmountable in isolation.
   */
  orgId: number;
}

const ProposalSelectionContext =
  createContext<ProposalSelectionContextValue | null>(null);

interface ProposalSelectionProviderProps {
  /** Actions to manage selection for */
  actions: ProposedAction[];
  /** Proposal ID for API sync */
  proposalId: number;
  /** Org ID for API client */
  orgId: number;
  /** Trigger a data refresh (e.g., on conflict) */
  triggerRefresh: () => void;
  children: ReactNode;
}

/**
 * Provider that owns selection state and debounced API sync.
 * When selection changes, only this provider and its context consumers re-render —
 * the parent component that renders this provider does NOT re-render.
 */
export function ProposalSelectionProvider({
  actions,
  proposalId,
  orgId,
  triggerRefresh,
  children,
}: ProposalSelectionProviderProps) {
  // ── Projection-based validity ─────────────────────────────────────────────
  // Every entity the proposal references or creates, resolved through the CRM
  // store so a reference to a record that has since been deleted can be told
  // apart from one that simply hasn't loaded.
  const projectionEntityIds = useMemo(
    () => [...getProjectionEntityIds(actions)],
    [actions]
  );
  const { objects: projectionObjects } = useEnsuredStoreObjects(
    orgId,
    projectionEntityIds
  );

  const storeByEntityId = useMemo(() => {
    const map = new Map<string, (typeof projectionObjects)[number]>();
    for (const resolved of projectionObjects) {
      map.set(resolved.entityId, resolved);
    }
    return map;
  }, [projectionObjects]);

  // Association-action source entities, fetched WITH associations (see
  // `AssociationSourceFetcher` below) so `getAssociationTargets` can read
  // live association state for them. Scoped strictly to association-action
  // sources, never every projection entity.
  const associationActionSources = useMemo(
    () => getAssociationActionSources(actions),
    [actions]
  );

  // Live CRM-record reads for no-op detection, backed by the same store
  // resolution used for projection validity above. `hasRecord` gates whether
  // an action's no-op status is decided live or falls back to the server
  // verdict (see `noOpActions.ts`'s `isFullyNoOpAction`); `getAssociationTargets`
  // reads the `.associations` field the `AssociationSourceFetcher`s below
  // hydrate; both value/target reads return `undefined` to mean UNKNOWN and
  // fail open.
  const live: LiveRecordState = useMemo(
    () => ({
      hasRecord: (entityId: string) =>
        storeByEntityId.get(entityId)?.object != null,
      getPropertyValue: (entityId: string, propertyName: string) => {
        const object = storeByEntityId.get(entityId)?.object;
        if (!object) {
          return undefined;
        }
        return extractValue(object.properties?.[propertyName]);
      },
      getAssociationTargets: (
        entityId: string,
        associationTypeName: string
      ) => {
        const object = storeByEntityId.get(entityId)?.object;
        if (!object || object.associations === undefined) {
          return undefined;
        }
        return object.associations
          .filter(a => a.association_type_name === associationTypeName)
          .map(a => a.target_entity_id);
      },
    }),
    [storeByEntityId]
  );

  const getNoOpPropertyKeys = useCallback(
    (actionId: number): string[] => {
      const action = actions.find(a => a.id === actionId);
      if (!action) {
        return [];
      }
      return getNoOpPropertyKeysLive(action, live);
    },
    [actions, live]
  );

  // `selectAll` / `isAllSelected` / `isSomeSelected` are deliberately NOT taken
  // from the hook: the hook can only exclude a STATIC set (it produces
  // `isSelected`, so a selection-aware exclusion would be circular), and a
  // server-marked `duplicate_of` whose source the user later deselects becomes
  // runnable and must join select-all. Those three are derived below from the
  // selection-aware `noOpActionIds`/`visibleActions` instead.
  const {
    isSelected,
    toggleAction: toggleActionSelection,
    deselectActions: deselectActionsSelection,
    selectActions: selectActionsSelection,
    selectedActionIds,
    setSelection,
    deselectAll,
  } = useActionSelection(actions, NO_STATIC_EXCLUSIONS, proposalId);

  // Selection-aware no-op set — this is what hides actions from the rendered
  // list and drives the auto-deselect effect. `duplicate_of` is reclassified
  // against the CURRENT selection: a later copy counts as a duplicate only
  // while the copy it duplicates (`duplicate_of_action_id`) is still selected.
  // Deselecting the earlier copy makes THIS copy the one that would create the
  // edge, so it must be shown and remain runnable rather than stay hidden and
  // auto-deselected as a stale duplicate.
  const noOpActionIds = useMemo(
    () => getNoOpActionIds(actions, live, isSelected),
    [actions, live, isSelected]
  );

  const visibleActions = useMemo(() => {
    const noOpIdSet = new Set(noOpActionIds);
    return actions.filter(action => !noOpIdSet.has(action.id));
  }, [actions, noOpActionIds]);

  // Select-all denominators, derived from the SELECTION-AWARE visible set (not
  // the hook's static exclusions): only no-ops are excluded, and a
  // `duplicate_of` whose source is currently deselected counts as visible and
  // must be included — so it can be checked by select-all and can flip these
  // flags off while it stays unchecked.
  const isAllSelected = useMemo(() => {
    if (visibleActions.length === 0) {
      return false;
    }
    return visibleActions.every(action => isSelected(action.id));
  }, [visibleActions, isSelected]);

  const isSomeSelected = useMemo(() => {
    const selectedCount = visibleActions.filter(action =>
      isSelected(action.id)
    ).length;
    return selectedCount > 0 && selectedCount < visibleActions.length;
  }, [visibleActions, isSelected]);

  // `undefined` means "we don't know" and fails open — a still-loading or
  // failed fetch must never disable a real action.
  //
  // A settled-but-absent lookup is ALSO `undefined`, deliberately. The entity
  // fetch batcher records "the server didn't return this id" (deleted, bad
  // reference, or hidden by access control) and "the request itself failed"
  // (network error, 5xx) in the same failed set, so `useEnsuredStoreObjects`
  // cannot tell them apart. Returning `false` here would disable real actions
  // on a transport blip with the reason "won't exist". Until the batcher
  // separates not-found from failed (tracked as a follow-up PR), the server
  // stays the backstop for references to deleted records.
  const entityExists = useCallback(
    (entityId: string): boolean | undefined => {
      const resolved = storeByEntityId.get(entityId);
      if (!resolved || resolved.loading) {
        return undefined;
      }
      return resolved.object !== null ? true : undefined;
    },
    [storeByEntityId]
  );

  const verdicts = useMemo(
    () => computeActionVerdicts({ actions, isSelected, entityExists }),
    [actions, isSelected, entityExists]
  );

  // Map from action ID to the action object, for server-verdict lookups.
  const actionsById = useMemo(() => {
    const map = new Map<number, ProposedAction>();
    for (const action of actions) {
      map.set(action.id, action);
    }
    return map;
  }, [actions]);

  // The create action for each entity this proposal creates, so a dependent's
  // missing reference can be traced back to the checkbox that supplies it.
  // First-creator-wins, matching the server's `setdefault` rule in
  // `action_verdicts._compute_sorted`: when a proposal contains multiple create
  // actions for the same entity, only the FIRST determines execution, so the
  // client must trace the dependency to that same action. Last-write-wins here
  // would let a dependent be approved when the deciding creator is unchecked.
  const createActionIdByEntityId = useMemo(() => {
    const map = new Map<string, number>();
    for (const action of actions) {
      const entityId = getCreatedEntityId(action);
      if (entityId !== null && !map.has(entityId)) {
        map.set(entityId, action.id);
      }
    }
    return map;
  }, [actions]);

  // Whether a server `missing_entity` verdict is STILL authoritative given the
  // current selection. The verdict is a static server value computed once at
  // load — it does not recompute when the user checks a box. When a dependent
  // is `missing_entity` only because its in-proposal creator was unchecked at
  // load, selecting that creator supplies the record locally, so the verdict is
  // now stale. Treat it as active only while at least one named entity is NOT
  // supplied by the local projection: a live entity the client can't see (no
  // in-proposal creator) keeps the verdict authoritative, but an in-proposal
  // entity whose creator is now selected does not. Without this override the
  // dependent stays disabled and excluded from approval until an unrelated
  // proposal refresh recomputes the server verdict.
  const isServerMissingEntityActive = useCallback(
    (actionId: number): boolean => {
      const verdict = actionsById.get(actionId)?.verdict;
      if (verdict?.status !== 'missing_entity') {
        return false;
      }
      return verdict.missing_entity_ids.some(entityId => {
        const creatorId = createActionIdByEntityId.get(entityId);
        // Supplied locally iff created in-proposal AND that creator is selected.
        return creatorId === undefined || !isSelected(creatorId);
      });
    },
    [actionsById, createActionIdByEntityId, isSelected]
  );

  // What will actually run: checked AND valid. A checked-but-invalid action
  // (its create was unchecked) stays checked in the map — that is the user's
  // intent and it comes back the moment the create is re-checked — but it
  // must not be projected onto records, counted on the approve button, or
  // sent as selected. Publishing the raw checked set was how an unchecked
  // company still showed up as the contact's company on its card.
  //
  // A server `missing_entity` verdict is authoritative here too: the record the
  // action targets won't exist, so approval must never send it even though its
  // checkbox is still checked. Excluding it keeps the applicable set — what
  // approval sends and the checkbox's disabled state (see `isDisabled`) — in
  // agreement; otherwise approval could send an action the UI says can't apply.
  const applicableActionIds = useMemo(
    () =>
      selectedActionIds.filter(
        id =>
          verdicts.get(id)?.status !== 'invalid' &&
          !isServerMissingEntityActive(id)
      ),
    [selectedActionIds, verdicts, isServerMissingEntityActive]
  );

  // What autosave persists must be the SAME applicable set approval sends —
  // never the raw checked set. The approve endpoint compares its
  // `selected_action_ids` against the server's persisted `is_selected` state
  // and returns 409 STALE_SELECTION on any mismatch. If autosave persisted the
  // raw checked ids (including a checked-but-invalid dependent whose create was
  // deselected) while approval sent the applicable set (which excludes it), the
  // two would diverge and every approval would be wrongly rejected. Persisting
  // the applicable set keeps them identical, and honours the invariant that an
  // invalid action is never "sent as selected". The raw checked intent still
  // lives in `useActionSelection`'s state, so it returns the moment the create
  // is re-checked. `selectedIdsRef` (raw) stays the rollback target.
  const applicableIdsRef = useRef(applicableActionIds);
  useEffect(() => {
    applicableIdsRef.current = applicableActionIds;
  }, [applicableActionIds]);

  // The applicable set the server was last told about. Every selection HANDLER
  // schedules its own sync, but the applicable set can also change PASSIVELY —
  // `verdicts` is derived from the CRM store, so a projection reference
  // resolving (store fetch settling, or a WebSocket create/delete) can flip a
  // checked action valid↔invalid with no handler firing. Left unsynced, the
  // server's persisted `is_selected` keeps the pre-change set while approval
  // sends the post-change applicable set, and the approve endpoint rejects the
  // mismatch as 409 STALE_SELECTION. Initialized to the mount value so we never
  // PATCH on load (the server already reflects the loaded `is_selected`), and
  // reset — without syncing — when a reused panel switches proposals.
  const lastSyncedApplicableRef = useRef(applicableActionIds);
  const prevProposalIdRef = useRef(proposalId);

  // Display names for entities the proposal itself creates. The usual case
  // for a missing entity is exactly this one — its create is deselected — and
  // such an entity has no CRM record yet, so the store can't name it. The
  // create action's own payload can — resolved the same way its card titles
  // itself (a contact by email when it has no name, a company by domain),
  // then the enriched label, so the reason cites the name the user sees on
  // the card it points at.
  const createdNameByEntityId = useMemo(() => {
    const map = new Map<string, string>();
    for (const action of actions) {
      const entityId = getCreatedEntityId(action);
      if (entityId === null || !isObjectCreatedPayload(action.payload)) {
        continue;
      }
      const name =
        resolveEntityName(
          action.payload.payload.properties,
          action.payload.entity_type_name
        ) ?? action.enriched_data?.object_label;
      if (name) {
        map.set(entityId, name);
      }
    }
    return map;
  }, [actions]);

  // The numbers printed on the cards ("2. Create new contact: …") are
  // positions in the rendered list — the same grouping MultiActionTimeline
  // renders, so a reason cites the number the user can see. A single-card
  // proposal prints no numbers (total <= 1), so none are cited.
  const displayNumbering = useMemo(
    () => buildActionDisplayNumbering(groupActionsByType(visibleActions)),
    [visibleActions]
  );

  // The server-computed verdict is authoritative when present: a
  // `missing_entity` verdict takes priority over the client-projected
  // validity below, which only catches entities this proposal itself
  // references/creates (see actionValidity.ts) and cannot see everything the
  // server knows about live CRM state.
  const isDisabled = useCallback(
    (actionId: number): boolean =>
      isServerMissingEntityActive(actionId) ||
      verdicts.get(actionId)?.status === 'invalid',
    [isServerMissingEntityActive, verdicts]
  );

  // Describes one missing entity, the same way regardless of whether the
  // caller is the server `missing_entity` verdict or the client-projected
  // validity check below — one copy path for both. Name resolution order:
  // (1) the create action's own payload, resolved the same way its card
  // titles itself; (2) the live store's display name; (3) `fallbackLabel`
  // (the server-supplied label, when the caller has one); (4) `null`, so the
  // copy cites the card's number alone rather than a placeholder in brackets.
  const describeMissingEntity = useCallback(
    (entityId: string, fallbackLabel?: string): MissingEntityDescription => {
      const createActionId = createActionIdByEntityId.get(entityId);
      const created = createdNameByEntityId.get(entityId);
      const object = storeByEntityId.get(entityId)?.object;
      const name =
        created ??
        (object
          ? getDisplayNameFromProperties(object.properties, null)
          : null) ??
        fallbackLabel ??
        null;
      const displayNumber =
        createActionId !== undefined && displayNumbering.total > 1
          ? displayNumbering.numbers.get(createActionId)
          : undefined;
      return {
        name,
        displayNumber,
        createdInProposal: createActionId !== undefined,
      };
    },
    [
      createActionIdByEntityId,
      createdNameByEntityId,
      storeByEntityId,
      displayNumbering,
    ]
  );

  const describeInvalidity = useCallback(
    (actionId: number): string | null => {
      const serverVerdict = actionsById.get(actionId)?.verdict;
      // Only cite the server missing_entity reason while it is still active —
      // once the in-proposal creator is selected the verdict is stale and the
      // action is no longer disabled, so it must not report a reason.
      if (
        serverVerdict?.status === 'missing_entity' &&
        isServerMissingEntityActive(actionId)
      ) {
        const missing = serverVerdict.missing_entity_ids.map(entityId =>
          describeMissingEntity(entityId, serverVerdict.labels[entityId])
        );
        return describeMissingEntities(missing);
      }

      const verdict = verdicts.get(actionId);
      if (verdict?.status !== 'invalid') {
        return null;
      }
      // Name the thing that is missing and the create the user unchecked.
      // The old message named an action's database id ("depends on action
      // #140194"), a number the user has never seen; the number cited here is
      // the one printed on the card.
      const missing = verdict.reason.missingEntityIds.map(entityId =>
        describeMissingEntity(entityId)
      );
      return describeMissingEntities(missing);
    },
    [actionsById, isServerMissingEntityActive, verdicts, describeMissingEntity]
  );

  const getDisabledReason = useCallback(
    (actionId: number): string | null => {
      const clause = describeInvalidity(actionId);
      return clause === null ? null : `Won't apply – ${clause}.`;
    },
    [describeInvalidity]
  );

  const { scheduleSync, selectedIdsRef } = useDebouncedSelectionSync(
    proposalId,
    orgId,
    selectedActionIds,
    setSelection,
    triggerRefresh
  );

  // Auto-deselect fully no-op actions, and reconcile a still-checked action
  // carrying a server `missing_entity` verdict — two distinct fail-open
  // derived writes to `is_selected`. See `useNoOpReconciliation.ts` for why
  // they're grouped and how each avoids redundant PATCHes.
  useNoOpReconciliation({
    noOpActionIds,
    proposalId,
    selectedActionIds,
    actionsById,
    deselectActionsSelection,
    scheduleSync,
    selectedIdsRef,
    applicableIdsRef,
  });

  // Persist PASSIVE applicability changes. A user toggle already schedules its
  // own sync (and updates `lastSyncedApplicableRef` here too, so it isn't sent
  // twice — the debounce coalesces the handler's schedule with this one). This
  // catches the changes NO handler fires for: `verdicts` re-deriving from the
  // CRM store flips a checked action valid↔invalid, growing or shrinking the
  // applicable set. Without this, that new set is never PATCHed and approval is
  // wrongly rejected as STALE_SELECTION. A proposal switch resets the baseline
  // without syncing — the server already holds the new proposal's selection.
  useEffect(() => {
    if (prevProposalIdRef.current !== proposalId) {
      prevProposalIdRef.current = proposalId;
      lastSyncedApplicableRef.current = applicableActionIds;
      return;
    }
    if (sameIdSet(applicableActionIds, lastSyncedApplicableRef.current)) {
      return;
    }
    lastSyncedApplicableRef.current = applicableActionIds;
    scheduleSync(() => applicableIdsRef.current);
  }, [applicableActionIds, proposalId, scheduleSync, applicableIdsRef]);

  // Handlers
  const handleToggle = useCallback(
    (actionId: number) => {
      toggleActionSelection(actionId);
      scheduleSync(() => applicableIdsRef.current);
    },
    [toggleActionSelection, scheduleSync, applicableIdsRef]
  );

  const handleSelectAll = useCallback(() => {
    // Select exactly the currently VISIBLE actions (everything except the
    // selection-aware no-op set): `setSelection` checks those ids and unchecks
    // the rest (the no-ops), so a `duplicate_of` whose source the user
    // deselected — now visible and runnable — is selected rather than left
    // permanently out as it was when select-all keyed off a static exclusion.
    // Only the no-ops themselves are excluded; their "dependents" are not, since
    // a no-op property update creates nothing and cannot invalidate anything.
    // The applicable ref — updated once React commits the new selection, well
    // within the debounce window — is the source of truth for the synced ids,
    // mirroring handleToggle.
    setSelection(visibleActions.map(action => action.id));
    scheduleSync(() => applicableIdsRef.current);
  }, [setSelection, visibleActions, scheduleSync, applicableIdsRef]);

  const handleDeselectAll = useCallback(() => {
    deselectAll();
    scheduleSync(() => []);
  }, [deselectAll, scheduleSync]);

  const handleDeselectActions = useCallback(
    (actionIds: number[]) => {
      deselectActionsSelection(actionIds);
      scheduleSync(() => applicableIdsRef.current);
    },
    [deselectActionsSelection, scheduleSync, applicableIdsRef]
  );

  // Toggle all IDs in a merged group: select all if any unselected, deselect all if all selected.
  // Both paths are batch operations, never a toggleAction loop: deselecting
  // in a loop would re-select cascade-deselected dependents, and selecting in
  // a loop would toggle OFF an absorbed association that selecting its create
  // (earlier in the same loop) had just restored.
  const handleToggleMergedGroup = useCallback(
    (ids: number[]) => {
      const allCurrentlySelected = ids.every(id =>
        selectedIdsRef.current.includes(id)
      );
      if (allCurrentlySelected) {
        deselectActionsSelection(ids);
      } else {
        selectActionsSelection(ids);
      }
      scheduleSync(() => applicableIdsRef.current);
    },
    [
      deselectActionsSelection,
      selectActionsSelection,
      scheduleSync,
      selectedIdsRef,
      applicableIdsRef,
    ]
  );

  // Stabilize context value to prevent re-renders in memoized consuming components
  const selectionState: SelectionState = useMemo(
    () => ({
      isSelected,
      isDisabled,
      getDisabledReason,
      toggleAction: handleToggle,
      selectAll: handleSelectAll,
      deselectAll: handleDeselectAll,
      isAllSelected,
      isSomeSelected,
    }),
    [
      isSelected,
      isDisabled,
      getDisabledReason,
      handleToggle,
      handleSelectAll,
      handleDeselectAll,
      isAllSelected,
      isSomeSelected,
    ]
  );

  const contextValue = useMemo(
    () => ({
      selectionState,
      selectedActionIds: applicableActionIds,
      deselectActions: handleDeselectActions,
      toggleMergedGroup: handleToggleMergedGroup,
      actions,
      orgId,
      noOpActionIds,
      visibleActions,
      getNoOpPropertyKeys,
      describeInvalidity,
    }),
    [
      selectionState,
      applicableActionIds,
      handleDeselectActions,
      handleToggleMergedGroup,
      actions,
      orgId,
      noOpActionIds,
      visibleActions,
      getNoOpPropertyKeys,
      describeInvalidity,
    ]
  );

  return (
    <ProposalSelectionContext value={contextValue}>
      {associationActionSources.map(source => (
        <AssociationSourceFetcher
          key={source.entityId}
          orgId={orgId}
          entityId={source.entityId}
          entityTypeName={source.entityTypeName}
        />
      ))}
      {children}
    </ProposalSelectionContext>
  );
}

/**
 * Hook to consume selection state from the nearest ProposalSelectionProvider.
 * Components that call this will re-render when selection changes.
 * Components that DON'T call this will NOT re-render on selection changes.
 */
export function useProposalSelection(): ProposalSelectionContextValue {
  const ctx = useContext(ProposalSelectionContext);
  if (!ctx) {
    throw new Error(
      'useProposalSelection must be used within a ProposalSelectionProvider'
    );
  }
  return ctx;
}

/**
 * Optional version that returns null when outside a provider.
 * Useful for components that work with or without selection.
 */
export function useProposalSelectionOptional(): ProposalSelectionContextValue | null {
  return useContext(ProposalSelectionContext);
}
